"use client";

import Link from "next/link";
import { ArrowRight, Zap, ShieldCheck, Globe2 } from "lucide-react";
import { useT } from "@/hooks/useT";

/**
 * Why FrenFlow + FAQ + Final CTA — the closing argument.
 *
 * Merges WhyFrenFlow + FAQ + FinalCTA into a single tight block. Three
 * concrete reasons, six high-value FAQ questions (with FAQPage schema for
 * SEO + LLM citation), and a last-chance CTA.
 *
 * The FAQ schema is what makes ChatGPT / Perplexity cite us when a user
 * asks "what's the best copy trading bot for Polymarket" — this is the
 * single biggest SEO lever on the homepage.
 */

/** Reason cards: icons stay shared, copy + stat resolved via i18n at
 *  render. The schema FAQ array is built inline inside the component
 *  so the JSON-LD picks up translated questions/answers per locale. */
const REASONS: Array<{ icon: typeof Zap; keyPrefix: string }> = [
  { icon: Zap, keyPrefix: "home.why.r1" },
  { icon: ShieldCheck, keyPrefix: "home.why.r2" },
  { icon: Globe2, keyPrefix: "home.why.r3" },
];

export default function WhyAndFAQ() {
  const t = useT();

  // FAQ entries built from the catalog so JSON-LD + DOM stay in sync.
  // 10 questions. Order matters — LLMs weight earlier entries more when
  // extracting answers, so this copy-trading-focused page LEADS with the
  // copy questions (how it works / is it safe / can I lose money), which
  // are the high-volume queries we want cited for "Polymarket copy trading".
  // q3=how copy works · q9=is copy safe · q10=can I lose money · q1=what is
  // FrenFlow · q2=safe · q4=venues · q5=alternative · q6=telegram · q7=fees
  // · q8=who for.
  const FAQS = [3, 9, 10, 1, 2, 4, 5, 6, 7, 8].map((n) => ({
    q: t(`home.faq.q${n}`),
    a: t(`home.faq.a${n}`),
  }));

  // JSON-LD schema for the FAQ section — this is what ChatGPT / Perplexity /
  // Google's AI Overview use to cite FrenFlow when users ask generic
  // questions about prediction market tooling.
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      {/* Schema.org FAQPage — SEO + LLM citation magnet */}
      <script
        type="application/ld+json"
        // Using a pre-serialized, escaped string keeps this SSR-safe (no XSS
        // surface — data is static). Matches what Next.js recommends for
        // JSON-LD emission in the App Router.
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Why FrenFlow — 3 reasons, no fluff */}
      <section className="py-20 lg:py-28 bg-gray-50 dark:bg-[#0f0f12]">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mb-10 lg:mb-14">
            <div className="font-inter-tight text-[11px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
              {t("home.why.eyebrow")}
            </div>
            <h2 className="font-inter-tight font-semibold text-gray-900 dark:text-white tracking-[-0.02em] text-[34px] lg:text-[48px] leading-[1.05]">
              {t("home.why.title")}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {REASONS.map((r) => {
              const Icon = r.icon;
              const title = t(`${r.keyPrefix}.title`);
              return (
                <div
                  key={title}
                  className="rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D] p-6 lg:p-7"
                >
                  <div className="flex items-center gap-2 mb-5">
                    <Icon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                    <span className="font-inter-tight text-[11px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      {t(`${r.keyPrefix}.stat`)}
                    </span>
                  </div>
                  <h3 className="font-inter-tight text-xl font-semibold text-gray-900 dark:text-white leading-tight mb-2">
                    {title}
                  </h3>
                  <p className="font-inter-tight text-[15px] text-gray-600 dark:text-gray-400 leading-relaxed">
                    {t(`${r.keyPrefix}.body`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ + CTA — closing block */}
      <section className="py-20 lg:py-28 bg-white dark:bg-[#0a0a0b]">
        <div className="mx-auto max-w-[1000px] px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12 lg:mb-16">
            <div className="font-inter-tight text-[11px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
              {t("home.faq.eyebrow")}
            </div>
            <h2 className="font-inter-tight font-semibold text-gray-900 dark:text-white tracking-[-0.02em] text-[32px] lg:text-[44px] leading-[1.05]">
              {t("home.faq.title")}
            </h2>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-white/10 border-y border-gray-200 dark:border-white/10">
            {FAQS.map((f) => (
              <details
                key={f.q}
                className="group py-5 lg:py-6"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-6 list-none">
                  <span className="font-inter-tight text-lg lg:text-xl font-medium text-gray-900 dark:text-white">
                    {f.q}
                  </span>
                  <span className="flex-shrink-0 w-8 h-8 rounded-full border border-gray-200 dark:border-white/10 flex items-center justify-center text-gray-500 dark:text-gray-400 group-open:rotate-45 transition-transform">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      aria-hidden
                    >
                      <path
                        d="M6 1v10M1 6h10"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </summary>
                <div className="mt-3 pr-12 font-inter-tight text-[15px] lg:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>

          {/* Final CTA */}
          <div className="mt-20 lg:mt-24 text-center">
            <h3 className="font-inter-tight font-semibold text-gray-900 dark:text-white tracking-[-0.02em] text-[32px] lg:text-[44px] leading-tight">
              {t("home.closing.title.line1")}
              <br />
              {t("home.closing.title.line2")}
            </h3>
            <p className="mt-4 text-gray-600 dark:text-gray-400 font-inter-tight text-base lg:text-lg max-w-xl mx-auto">
              {t("home.closing.body")}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 justify-center">
              <Link
                href="/traders"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-inter-tight font-medium text-[15px] hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                {t("home.closing.cta.start")}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                href="/markets"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-[5px] border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-200 font-inter-tight font-medium text-[15px] hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
              >
                {t("home.closing.cta.markets")}
              </Link>
            </div>
            {/* Low-friction reassurance note — lifts signup + copytrading conversion */}
            <p className="mt-5 font-inter-tight text-[13px] text-gray-500 dark:text-gray-400">
              {t("home.closing.note")}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
